import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  ArrowLeft, Mail, Phone, Globe, MapPin, Building2, Plus,
  Trash2, MoreHorizontal, Smartphone,
} from "lucide-react";
import InfoTab from "../components/tabs/InfoTab";
import PasswordsTab from "../components/tabs/PasswordsTab";
import NetworkTab from "../components/tabs/NetworkTab";
import HardwareTab from "../components/tabs/HardwareTab";
import DocsTab from "../components/tabs/DocsTab";
import TicketsTab from "../components/tabs/TicketsTab";
import { useConfirm } from "../contexts/ConfirmContext";

const TABS = [
  { id: "info", label: "Info" },
  { id: "passwords", label: "Passwörter", statKey: "passwords" },
  { id: "network", label: "Netzwerk", statKey: "network" },
  { id: "hardware", label: "Hardware", statKey: "hardware" },
  { id: "docs", label: "Dokumentation", statKey: "docs" },
  { id: "tickets", label: "Tickets", statKey: "tickets_total" },
];

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [customer, setCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [ticketFormTrigger, setTicketFormTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMore, setShowMore] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/customers/${id}`);
      setCustomer(r.data);
    } catch {
      navigate("/customers");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    const name = `${customer.first_name} ${customer.last_name}`;
    if (!(await confirm({
      title: "Kunde endgültig löschen",
      message: `"${name}" und alle zugehörigen Daten (Tickets, Passwörter, Netzwerk, Hardware, Dokumente) werden unwiderruflich gelöscht.`,
      confirmLabel: "Endgültig löschen",
    }))) return;
    await api.delete(`/customers/${id}`);
    navigate("/customers");
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Laden...</div>;
  if (!customer) return null;

  const stats = customer.stats || {};
  const fullAddress = [customer.address, [customer.zip, customer.city].filter(Boolean).join(" "), customer.country]
    .filter(Boolean).join(", ");
  const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;

  return (
    <div className="bg-background min-h-full">
      <div className="px-6 pt-6 max-w-[1400px] mx-auto w-full">
        <button
          data-testid="back-to-customers"
          onClick={() => navigate("/customers")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft size={14} /> Alle Kunden
        </button>

        {/* ── Compact Hero (single row) ────────────────────────── */}
        <div className="bg-card border border-border shadow-soft px-5 py-4 animate-in">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="initials-avatar w-12 h-12 text-base flex-shrink-0">
              {(customer.first_name?.[0] || "") + (customer.last_name?.[0] || "")}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold tracking-tight truncate">
                  {customer.first_name} {customer.last_name}
                </h1>
                <span className={customer.status === "active" ? "status-done text-[10px] font-bold" : "status-open text-[10px] font-bold"}>
                  {customer.status === "active" ? "Aktiv" : "Inaktiv"}
                </span>
                <span className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground">{customer.customer_number}</span>
                {customer.company && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Building2 size={11} />{customer.company}</span>
                )}
              </div>
              {/* Contact chips inline */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {customer.email && (
                  <a href={`mailto:${customer.email}`} data-testid="hero-email"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-accent text-xs transition-colors">
                    <Mail size={11} className="text-primary" /> {customer.email}
                  </a>
                )}
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-accent text-xs transition-colors">
                    <Phone size={11} className="text-primary" /> {customer.phone}
                  </a>
                )}
                {customer.mobile && (
                  <a href={`tel:${customer.mobile}`} className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-accent text-xs transition-colors">
                    <Smartphone size={11} className="text-primary" /> {customer.mobile}
                  </a>
                )}
                {customer.website && (
                  <a href={customer.website.startsWith("http") ? customer.website : `https://${customer.website}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-accent text-xs transition-colors">
                    <Globe size={11} className="text-primary" /> {customer.website}
                  </a>
                )}
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-accent text-xs transition-colors">
                    <MapPin size={11} className="text-primary" /> Karte
                  </a>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => { setActiveTab("tickets"); setTicketFormTrigger(v => v + 1); }}
                data-testid="hero-new-ticket"
                className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
              >
                <Plus size={14} /> Neues Ticket
              </button>
              <div className="relative">
                <button onClick={() => setShowMore(!showMore)} className="p-2 bg-secondary hover:bg-secondary/80 transition-colors" data-testid="hero-more-menu">
                  <MoreHorizontal size={15} />
                </button>
                {showMore && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMore(false)} />
                    <div className="absolute right-0 top-10 z-20 bg-card border border-border shadow-soft min-w-[180px]">
                      {customer.email && (
                        <a href={`mailto:${customer.email}`}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                          <Mail size={13} /> E-Mail senden
                        </a>
                      )}
                      <button onClick={remove}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10">
                        <Trash2 size={13} /> Kunde löschen
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs (sticky) ───────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border mt-4">
        <div className="px-6 max-w-[1400px] mx-auto">
          <div className="flex gap-1 overflow-x-auto" data-testid="customer-tabs">
            {TABS.map(t => {
              const count = t.statKey === "tickets_total" ? stats.tickets_open : (t.statKey ? stats[t.statKey] : null);
              return (
                <button
                  key={t.id} data-testid={`tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`tab-btn flex items-center gap-1.5 ${activeTab === t.id ? "active" : ""}`}
                >
                  {t.label}
                  {count !== null && count !== undefined && count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === t.id ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Tab Content ──────────────────────────────────────── */}
      <div className="px-6 py-5 max-w-[1400px] mx-auto">
        {activeTab === "info" && <InfoTab customer={customer} onUpdate={setCustomer} />}
        {activeTab === "passwords" && <PasswordsTab customerId={id} />}
        {activeTab === "network" && <NetworkTab customerId={id} />}
        {activeTab === "hardware" && <HardwareTab customerId={id} />}
        {activeTab === "docs" && <DocsTab customerId={id} />}
        {activeTab === "tickets" && <TicketsTab customerId={id} customerName={`${customer.first_name} ${customer.last_name}`} openFormTrigger={ticketFormTrigger} />}
      </div>
    </div>
  );
}
